/**
 * Race condition safe helpers for ride actions
 * Uses database functions to prevent simultaneous action conflicts
 */

import { supabase } from '../components/data/supabaseClient';

/**
 * Safely handle "I'm here" button click with race condition protection
 * @param {string} rideId - The ride ID
 * @param {string} userId - Current user ID
 * @param {Object} locationProof - Optional location proof object
 * @returns {Object} Result with success status and next action
 */
export const handleImHereAction = async (rideId, userId, locationProof = null) => {
  try {
    console.log('🔄 Handling "I\'m here" action with race condition protection...');
    
    const { data, error } = await supabase
      .rpc('handle_im_here_action', {
        ride_id_param: rideId,
        user_id_param: userId,
        location_proof_param: locationProof
      });

    if (error) {
      console.error('❌ Database function error:', error);
      throw error;
    }

    const result = data[0]; // RPC returns array
    
    if (result.conflict_detected) {
      console.warn('⚠️ Race condition detected:', result.message);
      return {
        success: false,
        conflictDetected: true,
        message: result.message,
        nextAction: 'refresh_state',
        rideData: result.ride_data
      };
    }

    if (!result.success) {
      console.error('❌ Action failed:', result.message);
      return {
        success: false,
        conflictDetected: false,
        message: result.message,
        nextAction: 'error',
        rideData: result.ride_data
      };
    }

    console.log('✅ "I\'m here" action completed successfully');
    return {
      success: true,
      conflictDetected: false,
      message: result.message,
      nextAction: result.next_action,
      rideData: result.ride_data
    };

  } catch (error) {
    console.error('❌ Error in handleImHereAction:', error);
    return {
      success: false,
      conflictDetected: false,
      message: error.message || 'An unexpected error occurred',
      nextAction: 'error',
      rideData: null
    };
  }
};

/**
 * Safely start waiting timer with conflict protection
 * @param {string} rideId - The ride ID
 * @param {string} userId - Current user ID
 * @param {number} durationMinutes - Timer duration in minutes
 * @returns {Object} Result with timer start status
 */
export const startWaitingTimer = async (rideId, userId, durationMinutes = 3) => {
  try {
    console.log('🔄 Starting waiting timer with race condition protection...');
    
    const { data, error } = await supabase
      .rpc('start_waiting_timer', {
        ride_id_param: rideId,
        user_id_param: userId,
        duration_minutes: durationMinutes
      });

    if (error) {
      console.error('❌ Timer start error:', error);
      throw error;
    }

    const result = data[0];
    
    console.log(result.timer_started ? '✅ Timer started successfully' : '⚠️ Timer could not be started');
    return {
      success: result.timer_started,
      message: result.message,
      rideData: result.ride_data
    };

  } catch (error) {
    console.error('❌ Error in startWaitingTimer:', error);
    return {
      success: false,
      message: error.message || 'Failed to start timer',
      rideData: null
    };
  }
};

/**
 * Safely update ride status with version checking
 * @param {string} rideId - The ride ID
 * @param {string} newStatus - New status to set
 * @param {string} userId - Current user ID
 * @param {string} expectedUpdatedAt - Expected updated_at timestamp for version checking
 * @returns {Object} Result with update status
 */
export const safeRideStatusUpdate = async (rideId, newStatus, userId, expectedUpdatedAt = null) => {
  try {
    console.log('🔄 Updating ride status with conflict protection...');
    
    const { data, error } = await supabase
      .rpc('safe_ride_status_update', {
        ride_id_param: rideId,
        new_status_param: newStatus,
        user_id_param: userId,
        expected_updated_at: expectedUpdatedAt
      });

    if (error) {
      console.error('❌ Status update error:', error);
      throw error;
    }

    const result = data[0];
    
    if (result.conflict_detected) {
      console.warn('⚠️ Version conflict detected:', result.error_message);
      return {
        success: false,
        conflictDetected: true,
        message: result.error_message,
        rideData: result.ride_data
      };
    }

    if (!result.success) {
      console.error('❌ Status update failed:', result.error_message);
      return {
        success: false,
        conflictDetected: false,
        message: result.error_message,
        rideData: result.ride_data
      };
    }

    console.log('✅ Ride status updated successfully');
    return {
      success: true,
      conflictDetected: false,
      message: result.error_message,
      rideData: result.ride_data
    };

  } catch (error) {
    console.error('❌ Error in safeRideStatusUpdate:', error);
    return {
      success: false,
      conflictDetected: false,
      message: error.message || 'Failed to update status',
      rideData: null
    };
  }
};

/**
 * Get current ride state with lock for critical operations
 * @param {string} rideId - The ride ID
 * @param {string} userId - Current user ID
 * @returns {Object} Ride data with user role and permissions
 */
export const getRideWithLock = async (rideId, userId) => {
  try {
    console.log('🔄 Getting ride state with lock...');
    
    const { data, error } = await supabase
      .rpc('get_ride_with_lock', {
        ride_id_param: rideId,
        user_id_param: userId
      });

    if (error) {
      console.error('❌ Get ride error:', error);
      throw error;
    }

    const result = data[0];
    
    console.log('✅ Ride state retrieved with lock');
    return {
      success: true,
      rideData: result.ride_data,
      userRole: result.user_role,
      canModify: result.can_modify
    };

  } catch (error) {
    console.error('❌ Error in getRideWithLock:', error);
    return {
      success: false,
      message: error.message || 'Failed to get ride state',
      rideData: null,
      userRole: null,
      canModify: false
    };
  }
};

/**
 * Handle race condition by refreshing component state
 * @param {Function} setRide - State setter for ride data
 * @param {Function} addToast - Toast notification function
 * @param {Function} t - Translation function
 * @param {Object} conflictResult - Result from conflicted operation
 */
export const handleRaceCondition = async (setRide, addToast, t, conflictResult) => {
  console.log('🔄 Handling race condition...');
  
  // Update local state with latest data
  if (conflictResult.rideData) {
    setRide(conflictResult.rideData);
  }
  
  // Show user-friendly message
  addToast({
    type: 'warning',
    title: t('ride_actions.conflict_detected'),
    description: conflictResult.message || t('ride_actions.state_updated_refresh')
  });
  
  // Return guidance for next action
  return {
    shouldRefresh: true,
    shouldRetry: false,
    message: conflictResult.message
  };
};

/**
 * Retry mechanism for failed operations
 * @param {Function} operation - The operation to retry
 * @param {number} maxRetries - Maximum number of retries
 * @param {number} delay - Delay between retries in ms
 * @returns {Object} Final result after retries
 */
export const retryOperation = async (operation, maxRetries = 3, delay = 1000) => {
  let lastError;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`🔄 Attempt ${attempt}/${maxRetries}...`);
      const result = await operation();
      
      if (result.success) {
        console.log(`✅ Operation succeeded on attempt ${attempt}`);
        return result;
      }
      
      if (result.conflictDetected) {
        console.log(`⚠️ Conflict detected on attempt ${attempt}, retrying...`);
        lastError = result;
        
        // Wait before retry
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, delay * attempt));
        }
        continue;
      }
      
      // Non-conflict error, don't retry
      console.log(`❌ Non-retryable error on attempt ${attempt}`);
      return result;
      
    } catch (error) {
      console.error(`❌ Exception on attempt ${attempt}:`, error);
      lastError = { success: false, message: error.message };
      
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, delay * attempt));
      }
    }
  }
  
  console.log(`❌ All ${maxRetries} attempts failed`);
  return lastError || { success: false, message: 'All retry attempts failed' };
};

export default {
  handleImHereAction,
  startWaitingTimer,
  safeRideStatusUpdate,
  getRideWithLock,
  handleRaceCondition,
  retryOperation
};